import {
  definitionForActiveReportGroup,
  getReportGroups,
  normalizeWidget,
  normalizeReportDefinition,
  resolveActiveReportGroupId,
  type ReportLayoutDefinition,
  type PresentationKind,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import type {
  SharedViewLayout,
  SharedViewWidgetShell,
} from "@/app/share/report/sharedReportTypes";

const EMPTY_LAYOUTS = {
  xs: { cards: [] },
  sm: { cards: [] },
  md: { cards: [] },
  lg: { cards: [] },
  xl: { cards: [] },
} as ReportLayoutDefinition["layouts"];

export function reportLayoutWidgetToSharedShell(
  widget: ReportLayoutDefinition["widgets"][number],
): SharedViewWidgetShell {
  const normalized = normalizeWidget(widget);

  return {
    identifier: normalized.id,
    source: normalized.source ?? null,
    presentation: (normalized.presentation ?? "TABLE") as PresentationKind,
    spec: normalized.spec ?? {},
    title: normalized.title ?? null,
    groupId: normalized.groupId ?? null,
    grid: { colSpan: 1, visible: true },
    defaultFilters: (normalized.defaultFilters ?? []).map((filter) => ({
      key: filter.key,
      type: filter.type,
      operator: filter.operator,
      value: filter.value,
      isLocked: filter.isLocked ?? false,
    })),
  };
}

export function resolveSharedWidgetPresentation(
  widget: SharedViewWidgetShell,
  reportLayoutWidget?: ReportLayoutDefinition["widgets"][number],
): PresentationKind {
  if (widget.presentation) {
    return widget.presentation as PresentationKind;
  }
  if (reportLayoutWidget?.presentation) {
    return reportLayoutWidget.presentation as PresentationKind;
  }
  return "TABLE";
}

function isChartPresentationKind(
  presentation: PresentationKind | string,
): boolean {
  return (
    presentation === "BAR_CHART" ||
    presentation === "LINE_CHART" ||
    presentation === "PIE_CHART"
  );
}

export { isChartPresentationKind };

export function sharedReportToDefinition(
  layout: SharedViewLayout,
): ReportLayoutDefinition {
  if (layout.definition && typeof layout.definition === "object") {
    return normalizeReportDefinition(layout.definition);
  }

  return normalizeReportDefinition({
    version: 2,
    groups: [{ id: "default", name: "Tab 1", layouts: EMPTY_LAYOUTS }],
    layouts: EMPTY_LAYOUTS,
    widgets: [],
  });
}

export function normalizeSharedViewLayout(raw: unknown): SharedViewLayout {
  const data = (raw ?? {}) as Partial<SharedViewLayout> & {
    groups?: Array<{ id?: string; name?: string }>;
  };

  return {
    name: data.name ?? null,
    definition:
      data.definition && typeof data.definition === "object"
        ? data.definition
        : null,
    entityNames: data.entityNames ?? [],
    defaultEntityIdentifiers: data.defaultEntityIdentifiers ?? [],
    groups: (data.groups ?? []).map((group) => ({
      id: group?.id ?? "default",
      name: group?.name ?? "Tab 1",
      layouts: EMPTY_LAYOUTS,
      widgets: [],
    })),
    widgets: [],
  };
}

export {
  definitionForActiveReportGroup,
  getReportGroups,
  resolveActiveReportGroupId,
};
